class Panel extends HTMLElement {
  constructor() {
    super();

    this.isOpen = this.getAttribute('default-open') === 'true';
    this.alignment = this.getAttribute('alignment') || 'right';
    this.additionalClasses = this.getAttribute('additionalClasses') || '';
    this.context = this.getAttribute('context');

    this.updatePanelClass();

    this.handleOpenEvent = this.handleOpenEvent.bind(this);
    this.handleCloseEvent = this.handleCloseEvent.bind(this);
    this.handleToggleEvent = this.handleToggleEvent.bind(this);
  }

  slidePanelOpen() {
    document.querySelectorAll('[slidePanelOpen]').forEach(btn => {
      btn.addEventListener('click', () => {
        // Save the last focused element (the button)
        this.lastFocusedElement = document.activeElement;

        document.querySelector('html').classList.add('overflow-hidden');
        document.querySelector('body').classList.add('overflow-hidden');
        document.querySelector('body').classList.add('no-scroll');
        document.querySelector('.main-product')?.classList.add('z-20');
        const triggerID = btn.getAttribute('slidePanelOpen');
        const slidePanel = document.querySelector(`slide-panel[slidePanelID="${triggerID}"]`);
        slidePanel.handleOpenEvent();

        // Move focus to the first focusable element in the mobile menu
        const firstFocusableElement = slidePanel.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusableElement) {
          firstFocusableElement.focus()
        }

        this.trapFocus(slidePanel)
      });
    });
  }

  slidePanelClose() {
    document.querySelectorAll('[slidePanelClose]').forEach(btn => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('slide-panel');
        if (panel) {
          document.querySelector('html').classList.remove('overflow-hidden');
          document.querySelector('body').classList.remove('overflow-hidden');
          document.querySelector('body').classList.remove('no-scroll');
          document.querySelector('.main-product')?.classList.remove('z-20');
          panel.handleCloseEvent();

          // Return focus to the last focused element or to the body
          if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
            this.lastFocusedElement.focus()
          } else {
            document.body.focus()
          }
          this.releaseFocusTrap()
        }
      });
    });
  }

  trapFocus(dialog) {
    const focusableElements = dialog.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) { // Shift + Tab
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement.focus(); // Move focus to last element if Shift + Tab on first element
          }
        } else {
          // eslint-disable-next-line
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement.focus(); // Move focus to first element if Tab on last element
          }
        }
      }
    });

    this.firstFocusableElement = firstFocusableElement
    this.lastFocusableElement = lastFocusableElement
  }

  // Function to release the focus trap
  releaseFocusTrap() {
    const dialog = this.querySelector('.search-bar');
    if (dialog) {
      dialog.removeEventListener('keydown', this.trapFocus);
    }
  }

  connectedCallback() {
    this.slidePanelOpen();
    this.slidePanelClose();

    this.triggers = {
      openTriggerEl: document.querySelector(this.getAttribute('openTriggerSelector')),
      closeTriggerEl: document.querySelector(this.getAttribute('closeTriggerSelector')),
      toggleEl: document.querySelector(this.getAttribute('toggleSelector')),
    };

    const { openTriggerEl, closeTriggerEl, toggleEl } = this.triggers;

    if (openTriggerEl) openTriggerEl.addEventListener('click', this.handleOpenEvent);
    if (closeTriggerEl) closeTriggerEl.addEventListener('click', this.handleCloseEvent);
    if (toggleEl) toggleEl.addEventListener('click', this.handleToggleEvent);
    if (this.context) {
      document.addEventListener(`${this.context}:open`, this.handleOpenEvent);
      document.addEventListener(`${this.context}:close`, this.handleCloseEvent);
    }

    this.initElements();
    this.setupInputMapping();
  }

  initElements() {
    this.wristCircumferenceInput = this.querySelector('#wrist-circumference') || null;
    this.braceletSizeInput = this.querySelector('#bracelet-size') || null;
    this.panelForm = this.querySelector('.panel-content__form') || null;
    this.circumferenceData = this.panelForm?.getAttribute('data-circumference') || null;
    this.sizeData = this.panelForm?.getAttribute('data-size') || null;
  }

  setupInputMapping() {
    if (this.circumferenceData && this.sizeData) {
      const circumferences = this.circumferenceData.split(',').map(circumference => circumference.trim());
      const sizes = this.sizeData.split(',').map(size => size.trim());

      const mapping = {};
      for (let i = 0; i < circumferences.length; i += 1) {
        mapping[circumferences[i]] = sizes[i];
      }

      this.wristCircumferenceInput.addEventListener('input', () => {
        const inputValue = this.wristCircumferenceInput.value.trim();
        if (Object.prototype.hasOwnProperty.call(mapping, inputValue)) {
          this.braceletSizeInput.value = mapping[inputValue];
        } else {
          this.braceletSizeInput.value = '';
        }
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'default-open') {
      this.isOpen = newValue === 'true';
      this.updatePanelClass();
    }
  }

  disconnectedCallback() {
    const { openTriggerEl, closeTriggerEl, toggleEl } = this.triggers;

    if (openTriggerEl) openTriggerEl.removeEventListener('click', this.handleOpenEvent);
    if (closeTriggerEl) closeTriggerEl.removeEventListener('click', this.handleCloseEvent);
    if (toggleEl) toggleEl.removeEventListener('click', this.handleToggleEvent);

    if (this.context) {
      document.removeEventListener(`${this.context}:open`, this.handleOpenEvent);
      document.removeEventListener(`${this.context}:close`, this.handleCloseEvent);
    }
  }

  handleOpenEvent() {
    this.isOpen = true;
    this.updatePanelClass();
  }

  handleCloseEvent() {
    this.isOpen = false;
    this.updatePanelClass();
  }

  handleToggleEvent() {
    this.isOpen = !this.isOpen;
    this.updatePanelClass();
  }

  static get observedAttributes() {
    return ['default-open'];
  }

  updatePanelClass() {
    const baseClass = 'block w-full h-full fixed top-0 bottom-0 z-40 bg-white';
    const alignmentClass = this.alignment === 'popup'
      ? 'right-0 left-0 scale-0'
      : `${this.alignment
      }-0 max-w-panel`;
    const closedClass = this.isOpen
      ? 'translate-x-0 block active visible'
      : this.alignment === 'left'
        ? '-translate-x-full invisible'
        : 'translate-x-full invisible';
    this.className = `${baseClass} ${alignmentClass} ${closedClass} ${this.additionalClasses
      }`;
  }
}

customElements.define('slide-panel', Panel);
